import { useRef, useState } from 'react';
import { haptic } from '../../lib/haptic';

type Props = {
  label: string;
  loadingLabel: string;
  onConfirm: () => void;
};

const TRACK_HEIGHT = 64;
const THUMB_SIZE = 52;
const PADDING = 6;
const COMMIT_THRESHOLD = 0.82;

/**
 * Drag-right-to-confirm primitive used by lifecycle gates (start trip, end trip).
 *
 * v2 visual: mint-soft track + visible 52×52 thumb (chevron-right icon) that
 * the user slides across. The trail/fill grows under the thumb as it moves.
 * Drag past 82% of the available travel distance to commit; anything less
 * snaps back.
 *
 * The gesture API is unchanged from the previous "progress bar fill" visual:
 * threshold stays at 82%, haptics fire at the same points, onConfirm signature
 * is identical. setPointerCapture keeps the drag forgiving so the user's
 * finger can stray outside the track without dropping the gesture.
 */
export function SlideToConfirm({ label, loadingLabel, onConfirm }: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const progressRef = useRef(0);
  const crossedThresholdRef = useRef(false);
  // Cache the travel distance at pointerdown — it doesn't change mid-drag.
  const dragRef = useRef<{ startX: number; travelDistance: number } | null>(null);

  function updateProgress(p: number) {
    progressRef.current = p;
    setProgress(p);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (loading) return;
    const rect = trackRef.current!.getBoundingClientRect();
    const travelDistance = rect.width - 2 * PADDING - THUMB_SIZE;
    dragRef.current = { startX: e.clientX, travelDistance };
    crossedThresholdRef.current = false;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current || loading) return;
    const dx = e.clientX - dragRef.current.startX;
    const next = Math.max(0, Math.min(1, dx / dragRef.current.travelDistance));
    updateProgress(next);
    if (!crossedThresholdRef.current && next >= COMMIT_THRESHOLD) {
      crossedThresholdRef.current = true;
      haptic.snap();
    }
  }

  function onPointerUp() {
    if (!dragRef.current || loading) return;
    if (progressRef.current >= COMMIT_THRESHOLD) {
      updateProgress(1);
      setLoading(true);
      haptic.success();
      onConfirm();
      setTimeout(() => { setLoading(false); updateProgress(0); }, 600);
    } else {
      updateProgress(0);
    }
    crossedThresholdRef.current = false;
    dragRef.current = null;
  }

  const dragging = !!dragRef.current;
  // Thumb travels from x=PADDING (resting) to x=trackWidth-PADDING-THUMB_SIZE.
  // Travel distance in CSS = 100% - (2*PADDING + THUMB_SIZE) = 100% - TRACK_HEIGHT.
  const thumbLeft = `calc(${progress * 100}% - ${progress * TRACK_HEIGHT}px + ${PADDING}px)`;
  // Trail fills from the track's inner-left edge to the thumb's right edge.
  const trailWidth = `calc(${progress * 100}% - ${progress * TRACK_HEIGHT}px + ${THUMB_SIZE}px)`;

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="button"
      aria-label={label}
      style={{
        position: 'relative',
        height: TRACK_HEIGHT,
        padding: PADDING,
        borderRadius: 'var(--radius-pill)',
        background: 'var(--mint-soft)',
        border: '1px solid var(--mint-line)',
        userSelect: 'none',
        touchAction: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: PADDING,
          left: PADDING,
          height: `calc(100% - ${2 * PADDING}px)`,
          width: trailWidth,
          background: 'var(--color-primary)',
          borderRadius: 'var(--radius-pill)',
          transition: dragging ? 'none' : 'width 200ms ease',
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          paddingLeft: TRACK_HEIGHT,
          paddingRight: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: loading ? '#FFFFFF' : 'var(--green-700)',
          fontWeight: 700,
          fontSize: 13,
          textTransform: 'uppercase',
          letterSpacing: '0.10em',
          pointerEvents: 'none',
          opacity: loading ? 1 : Math.max(0, 1 - progress * 1.5),
          transition: dragging ? 'none' : 'opacity 200ms ease, color 200ms ease',
          zIndex: 3,
        }}
      >
        {loading ? loadingLabel : label}
      </div>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: PADDING,
          left: thumbLeft,
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          borderRadius: '50%',
          background: 'var(--color-primary)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(46, 158, 101, 0.45)',
          transition: dragging ? 'none' : 'left 200ms ease',
          zIndex: 2,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </div>
    </div>
  );
}
